import {
  type DeployLlmKeyRow,
  fetchLlmKeys,
  revokeLlmKey,
  selectLlmKeysForTable,
} from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { llmKeyDetailUrl } from "@app/routes";
import { Button } from "@app/ui/shared/button";
import { DataTable } from "@app/ui/shared/datatable";
import {
  CurrencyCell,
  DataTableCell,
  DateTimeCell,
  EnvironmentCell,
  PillCell,
} from "@app/ui/shared/datatable/cells";
import type {
  DataTableCellRendererProps,
  DataTableColDef,
} from "@app/ui/shared/datatable/datatable";
import { FormGroup } from "@app/ui/shared/form-group";
import { Group } from "@app/ui/shared/group";
import { IconChevronDown, IconChevronRight } from "@app/ui/shared/icons";
import { Input } from "@app/ui/shared/input";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
} from "@app/ui/shared/modal";
import { ModalWrapper } from "@app/ui/shared/modal-portal";
import { tokens } from "@app/ui/shared/tokens";
import { useEffect, useState } from "react";

/* Legacy LLM keys. Customers move to v2 on their own schedule, so they need to
 * see which v1 keys they still have in order to know what to replace and, once
 * they have, what to revoke.
 *
 * Delete this file and its single usage in llm-gateway.tsx once v1 keys are
 * retired.
 */

const RevokeLegacyKeyModal = ({
  isOpen,
  onClose,
  llmKey,
}: {
  isOpen: boolean;
  onClose: () => void;
  llmKey: DeployLlmKeyRow | null;
}) => {
  const dispatch = useDispatch();
  const loader = useLoader(revokeLlmKey);
  const orgId = useSelector(selectOrganizationSelectedId);
  const [confirm, setConfirm] = useState("");

  useLoaderSuccess(loader, onClose);

  useEffect(() => {
    if (!isOpen) return;
    setConfirm("");
  }, [isOpen]);

  if (!isOpen || !llmKey) return null;

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={onClose} className="min-w-[500px]">
        <ModalHeading title="Revoke Legacy LLM Key" onClose={onClose} />
        <ModalBody>
          <p className="text-gray-700 mb-4">
            This will permanently revoke <strong>{llmKey.handle}</strong>. Any
            applications using this key will immediately lose access to the
            legacy gateway. This action cannot be undone.
          </p>
          <FormGroup
            label={`Type "${llmKey.handle}" to confirm`}
            htmlFor="revoke-legacy-confirm"
          >
            <Input
              id="revoke-legacy-confirm"
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={llmKey.handle}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter
          actions={[
            <Button key="cancel" variant="white" onClick={onClose}>
              Cancel
            </Button>,
            <Button
              key="revoke"
              variant="delete"
              disabled={confirm !== llmKey.handle}
              isLoading={loader.isLoading}
              onClick={() => dispatch(revokeLlmKey({ id: llmKey.id, orgId }))}
            >
              Revoke Key
            </Button>,
          ]}
        />
      </Modal>
    </ModalWrapper>
  );
};

const colDefs = (
  onRevoke: (key: DeployLlmKeyRow) => void,
): DataTableColDef<DeployLlmKeyRow>[] => [
  {
    field: "handle",
    headerName: "Key",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <DataTableCell
        label={data.handle}
        href={llmKeyDetailUrl(data.id)}
        description={data.note ?? undefined}
      />
    ),
  },
  {
    field: "status",
    headerName: "Status",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <PillCell
        value={data.status === "ACTIVE" ? "Active" : "Revoked"}
        valueToVariantMap={{ Active: "success", Revoked: "error" }}
      />
    ),
  },
  {
    field: "envHandle",
    headerName: "Environment",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <EnvironmentCell id={data.environmentId} hideIcon={true} />
    ),
  },
  {
    field: "createdAt",
    headerName: "Created At (UTC)",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <DateTimeCell date={data.createdAt} />
    ),
  },
  {
    field: "spend",
    headerName: "Spend",
    sort: "desc",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <CurrencyCell value={data.spend} />
    ),
  },
  {
    colId: "actions",
    headerName: "Actions",
    sortable: false,
    width: 100,
    resizable: false,
    suppressAutoSize: true,
    suppressSizeToFit: true,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployLlmKeyRow>) => (
      <Button
        variant="delete"
        size="sm"
        disabled={data.status === "REVOKED"}
        onClick={() => onRevoke(data)}
      >
        Revoke
      </Button>
    ),
  },
];

export const LegacyLlmKeySection = () => {
  const orgId = useSelector(selectOrganizationSelectedId);
  const [isExpanded, setIsExpanded] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState<DeployLlmKeyRow | null>(null);

  useQuery(fetchLlmKeys({ orgId }));
  const llmKeys = useSelector(selectLlmKeysForTable);

  if (llmKeys.length === 0) return null;

  return (
    <Group size="sm">
      <button
        type="button"
        className="flex items-center gap-2 w-fit text-left"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <IconChevronDown variant="sm" />
        ) : (
          <IconChevronRight variant="sm" />
        )}
        <span className={tokens.type.h4}>Legacy Keys ({llmKeys.length})</span>
      </button>

      {isExpanded ? (
        <>
          <p className="text-gray-600">
            These keys work against the previous LLM Gateway and can't be used
            with the endpoint above. Create replacements and revoke these once
            your applications have migrated.
          </p>

          <DataTable<DeployLlmKeyRow>
            rowData={llmKeys}
            colDefs={colDefs(setKeyToRevoke)}
            queryParamPrefix="legacy"
          />
        </>
      ) : null}

      <RevokeLegacyKeyModal
        isOpen={keyToRevoke !== null}
        onClose={() => setKeyToRevoke(null)}
        llmKey={keyToRevoke}
      />
    </Group>
  );
};
